import { css } from '@emotion/react';

import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import type {
  AnchorHTMLAttributes,
  ReactNode,
} from 'react';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';

const BUTTON_STYLE = css`
  label: BUTTON_STYLE;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0 ${LAYOUT2.spacing2}px;
  gap: ${LAYOUT2.spacing1}px;

  ${TYPOGRAPHY2.xSmallBold}
  text-decoration: none;
  text-transform: uppercase;
  text-wrap: nowrap;

  cursor: pointer;
  border: none;

  transition: background 0.2s ease;
`;

type ButtonStyleObject = {
  color: string;
  backgroundColor: string;
  outlineColor?: string;
  hoverBackgroundColor: string;
  disabledBackgroundColor?: string;
};
const ButtonStyleColorsPrep = { // ButtonStylesPrep??? typescript is dumb; this is a quick fix for correctly typing the optional disabledBackgroundColor
  // The 2026 styleguide's active-button scheme: accent ("lemon") fill, ink text.
  lemon: {
    color: styleguideColors.ink,
    outlineColor: styleguideColors.ink,
    backgroundColor: styleguideColors.accent,
    hoverBackgroundColor: styleguideColors.accentDeep,
    disabledBackgroundColor: COLORS2.alwaysDark.gray,
  },
  green: {
    color: COLORS2.alwaysDark.charcoal,
    backgroundColor: COLORS2.alwaysDark.green,
    hoverBackgroundColor: COLORS2.alwaysDark.darkGreen,
    disabledBackgroundColor: COLORS2.alwaysDark.gray,
  },
  red: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.red,
    hoverBackgroundColor: COLORS2.alwaysDark.darkRed,
    disabledBackgroundColor: COLORS2.alwaysDark.gray,
  },
  yellow: {
    color: COLORS2.alwaysDark.black,
    backgroundColor: COLORS2.alwaysDark.yellow,
    hoverBackgroundColor: COLORS2.alwaysDark.darkYellow,
    disabledBackgroundColor: COLORS2.alwaysDark.gray,
  },
  darkYellow: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkYellow,
    hoverBackgroundColor: COLORS2.alwaysDark.yellow,
    disabledBackgroundColor: COLORS2.alwaysDark.darkGray,
  },
  greenLight: {
    color: COLORS2.alwaysDark.black,
    backgroundColor: COLORS2.alwaysDark.lightGreen,
    hoverBackgroundColor: COLORS2.alwaysDark.green,
    disabledBackgroundColor: COLORS2.alwaysDark.lightGray,
  },
  blue: {
    color: COLORS2.alwaysDark.black,
    backgroundColor: COLORS2.alwaysDark.blue,
    hoverBackgroundColor: COLORS2.alwaysDark.darkBlue,
    disabledBackgroundColor: COLORS2.alwaysDark.gray,
  },
  darkBlue: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkBlue,
    hoverBackgroundColor: COLORS2.alwaysDark.blue,
    disabledBackgroundColor: COLORS2.alwaysDark.darkGray,
  },

  // Black & White
  black: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.black,
    hoverBackgroundColor: COLORS2.alwaysDark.darkestGray,
  },
  charcoal: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.charcoal,
    hoverBackgroundColor: COLORS2.alwaysDark.darkGray,
  },
  white: {
    color: COLORS2.alwaysDark.charcoal,
    outlineColor: COLORS2.alwaysDark.gray,
    backgroundColor: COLORS2.alwaysDark.white,
    disabledBackgroundColor: COLORS2.alwaysDark.lightGray,
    hoverBackgroundColor: COLORS2.alwaysDark.lightGray,
  },
  gray: {
    color: COLORS2.alwaysDark.black,
    backgroundColor: COLORS2.alwaysDark.gray,
    hoverBackgroundColor: COLORS2.alwaysDark.darkGray,
  },

  darkestGray: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkestGray,
    hoverBackgroundColor: COLORS2.alwaysDark.black,
  },
  darkGray: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkGray,
    hoverBackgroundColor: COLORS2.alwaysDark.gray,
  },
  darkerGray: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkerGray,
    hoverBackgroundColor: COLORS2.alwaysDark.darkGray,
  },
  darkGreen: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkGreen,
    hoverBackgroundColor: COLORS2.alwaysDark.green,
  },
  darkOrange: {
    color: COLORS2.alwaysDark.white,
    backgroundColor: COLORS2.alwaysDark.darkOrange,
    hoverBackgroundColor: COLORS2.alwaysDark.orange,
  },
};
export type ButtonColorStyle = keyof typeof ButtonStyleColorsPrep;
const ButtonStyleColors = ButtonStyleColorsPrep as Record<ButtonColorStyle, ButtonStyleObject>;

const BUTTON_STYLE_SIZES = {
  small: css`
    label: BUTTON_STYLE_SIZES_SMALL;
    ${TYPOGRAPHY2.xSmallBold}
    padding: 2px ${LAYOUT2.spacing1}px;
    height: ${LAYOUT2.spacing3}px;
    width: ${LAYOUT2.spacing3}px !important
    min-width: ${LAYOUT2.spacing3}px !important;
    min-height: ${LAYOUT2.smallButtonHeight}px !important;
  `,
  medium: css`
    label: BUTTON_STYLE_SIZES_MEDIUM;
    ${TYPOGRAPHY2.xSmallBold}
    min-height: ${LAYOUT2.mediumButtonHeight}px !important;
  `,
  large: css`
    label: BUTTON_STYLE_SIZES_LARGE;
    ${TYPOGRAPHY2.mediumBold};
    min-height: ${LAYOUT2.largeButtonHeight}px !important;
  `,
};

const getButtonStyle = (buttonStyle: ButtonColorStyle) =>
  css`
  background: ${ButtonStyleColors[buttonStyle].backgroundColor} !important;
  color: ${ButtonStyleColors[buttonStyle].color} !important;
  outline: 1px solid ${ButtonStyleColors[buttonStyle].outlineColor ?? 'transparent'} !important;
  &:hover {
    background: ${ButtonStyleColors[buttonStyle].hoverBackgroundColor} !important;
  }
`;

const getDisabledButtonStyle = (buttonStyle: ButtonColorStyle) =>
  css`
  opacity: 0.5;
  cursor: default;
  background: ${ButtonStyleColors[buttonStyle].disabledBackgroundColor ?? ButtonStyleColors[buttonStyle].backgroundColor} !important;
  &:hover {
    background: ${ButtonStyleColors[buttonStyle].disabledBackgroundColor ?? ButtonStyleColors[buttonStyle].backgroundColor} !important;
  }
`;

type ButtonSizes = 'small' | 'medium' | 'large';
type ButtonProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type'> & {
  children: ReactNode;
  disabled?: boolean;
  href?: string;
  onClick?: () => void;
  openInNewTab?: boolean;
  size?: ButtonSizes;
  type?: 'button' | 'submit' | 'reset';
  width?: string;
  buttonStyle?: ButtonColorStyle;
  flex?: number;
};

export const Button = ({
  children,
  openInNewTab,
  href,
  width,
  flex,
  buttonStyle = 'green',
  size = 'medium',
  type = 'button',
  onClick,
  disabled = false,
  ...anchorProps
}: ButtonProps) => {
  if (href) {
    return (
      <a
        href={href}
        css={[
          BUTTON_STYLE,
          BUTTON_STYLE_SIZES[size],
          getButtonStyle(buttonStyle),
          disabled && getDisabledButtonStyle(buttonStyle),
          {
            width: width || 'auto',
            flex: flex || undefined,
          },
        ]}
        target={openInNewTab ? '_blank' : '_self'}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={onClick}
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      css={[
        BUTTON_STYLE,
        BUTTON_STYLE_SIZES[size],
        { width: width || 'auto', flex: flex || undefined, padding: flex === 1 ? 0 : undefined },
        getButtonStyle(buttonStyle),
        disabled && getDisabledButtonStyle(buttonStyle),
      ]}
      onClick={e => {
        e.stopPropagation();
        onClick?.();
      }}
      disabled={disabled}
    >
      {children}
    </button>
  );
};
